/* istanbul ignore file */
import React, { useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import UserContext from '@context/User/user-context';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import useAlert, { TYPES } from '@hooks/useAlert/useAlert';
import useMutationCreateCustomerBoard from '@hooks/Board/useMutationCreateCustomerBoard';
import CreateBoardModalContext, { ICreateBoardModal } from '@context/Modal/create-board-modal-context';
import LoginRegisterModalContext from '@context/Modal/login-register-modal-context';
import CreateBoardModal from '@components/shared/modals/CreateBoardModal/CreateBoardModal';
import styles from './CreateBoardModalProvider.module.scss';

const SESSION_STORAGE_KEY = 'havenly.pendingCreateBoard';

const CreateBoardModalProvider:React.FunctionComponent = ({ children }) => {
  const [
    modalState,
    setModalState,
  ] = useState<ICreateBoardModal | null>(null);
  const [busy, setBusy] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const { open } = useContext(LoginRegisterModalContext);
  const router = useRouter();
  const {
    user, isLoading,
  } = useContext(UserContext);

  const alert = useAlert(TYPES.SUCCESS);
  const errorAlert = useAlert(TYPES.ERROR);
  const [
    mutationCreateCustomerBoard,
  ] = useMutationCreateCustomerBoard();

  useEffect(() => {
    if (modalState && !isLoading && !user) {
      open({
        closable: true,
        onLoginSuccess: () => {},
        onRegisterSuccess: () => {},
        magicLinkRedirectUrl: router.asPath,
        source: modalState.source,
      });
    }
  }, [user, isLoading, modalState]);

  useEffect(() => {
    const pendingAddToSession = window.sessionStorage
      .getItem(`${SESSION_STORAGE_KEY} ${window.location.href}`);

    if (pendingAddToSession) {
      window.sessionStorage.removeItem(`${SESSION_STORAGE_KEY} ${window.location.href}`);
      setModalState(JSON.parse(pendingAddToSession));
    }
  }, []);

  const closeModal = () => {
    setBusy(false);
    setNewBoardName('');
    setModalState(null);
  };

  const createBoard = async () => {
    setBusy(true);

    // prepare muation args
    const customerBoardInput = {
      name: newBoardName,
      items: [],
    };

    let result;
    try {
      result = await (mutationCreateCustomerBoard as any)(
        customerBoardInput,
      );
    } catch (err) {
      errorAlert(`Failed to create ${newBoardName}.`, 3000);
      setBusy(false);
      return;
    }
    if (result.data && result.data.createCustomerBoard) {
      analyticsTrack(
        'explore_board_created',
        {
          boardId: result.data.createCustomerBoard.id,
        },
      );

      const board = result.data.createCustomerBoard;
      const alertMessage = (
        <>
          {`Created ${board.name}.`}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <a className={ styles.AlertLink } href={ `/home/boards/${board.id}` }>
            View
          </a>
        </>
      );

      alert(alertMessage, 3000);
    }

    closeModal();
  };

  return (
    <CreateBoardModalContext.Provider
      value={{
        open: setModalState,
      }}
    >
      {modalState && user && (
        <CreateBoardModal
          busy={ busy }
          newBoardName={newBoardName}
          onClose={closeModal}
          onCreate={createBoard}
          setNewBoardName={setNewBoardName}
        />
      )}
      {children}
    </CreateBoardModalContext.Provider>
  );
};

export default CreateBoardModalProvider;
